import { AssignmentsProvider } from "@/assignments/useAssignmentsGet";
import { UserProperties } from "@/types/models/User/v3";
import { ClassroomsProvider } from "@/classrooms/useClassroomsContext";
import { ReadAloudContextProvider } from "@/view/ReadAloudControls/useReadAloudMode";
import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";
import { AccessibilityControlsContextProvider } from "@/view/utils/useAccessibilityControlsContext";
import { AnnotationContextProvider } from "@/view/utils/useAnnotationsState";
import { AssignmentActivitySelectionProvider } from "@/view/utils/useAssignmentActivitySelectionContext";
import { AssignmentReducerProvider } from "@/view/utils/useAssignmentReducerContext";
import { AttachmentsContextProvider } from "@/view/utils/useAttachmentsContext";
import { FocusManagerContextProvider } from "@/view/utils/useFocusManager/useFocusManager";
import { PollContextProvider } from "@/view/utils/usePollContext/usePollContext";
import { PresentModeContextProvider } from "@/view/utils/usePresentModeContext/usePresentModeContext";
import { ReadingSkillsContextProvider } from "@/view/utils/useReadingSkillsContext/useReadingSkillsContext";
import { StudentPreviewContextProvider } from "@/view/utils/useStudentPreviewContext/useStudentPreviewContext";
import { UserContentUIContextProvider } from "@/view/utils/useUserContentUIContext";
import { VideoEventBusContextProvider } from "@/view/utils/useVideoEventBus";

import { InitialActivitySelections } from "@/view/utils/useAssignmentActivitySelectionContext/useAssignmentActivitySelectionContext";
import { TranslatorControlsContextProvider } from "@/view/utils/useTranslatorControlsContext";
import { ActivityPanelContextProvider } from "../useActivityPanel/useActivityPanel";
import { GuidedHighlightingStudentContextProvider } from "../useGuidedHighlightingStudent/useGuidedHighlightingStudent";
import { GuidedHighlightingStudentAnswerContextProvider } from "../useGuidedHighlightingStudentAnswer/useGuidedHighlightingStudentAnswer";

type ViewPageProviderProps = {
  children: React.ReactNode;
  contentType: string;
  contentId: string;
  currentAssignment: object;
  currentLevelLanguage: string;
  isBannerModeActive: boolean;
  isLiteContent: boolean;
  isPremiumContent: boolean;
  legacyArticleSlug: string;
  levelId: string;
  rawText: INode;
  selectedAssignmentId: string;
  setIsActivityPanelExpanded: () => void;
  initialActivitySelections: InitialActivitySelections;
  userProperties: UserProperties;
};

// Include any individual providers here, our view page provider wraps the whole view page.
const ViewPageProviders = ({
  children,
  contentId,
  contentType,
  currentAssignment,
  currentLevelLanguage,
  isBannerModeActive,
  isLiteContent,
  isPremiumContent,
  legacyArticleSlug,
  levelId,
  rawText,
  selectedAssignmentId,
  setIsActivityPanelExpanded,
  userProperties,
  initialActivitySelections,
}: ViewPageProviderProps) => {
  return (
    <FocusManagerContextProvider>
      <PresentModeContextProvider
        contentType={contentType}
        isBannerModeActive={isBannerModeActive}
      >
        <ReadAloudContextProvider>
          <AccessibilityControlsContextProvider>
            <TranslatorControlsContextProvider>
              <VideoEventBusContextProvider>
                <AttachmentsContextProvider contentId={contentId}>
                  <GuidedHighlightingStudentContextProvider>
                    <StudentPreviewContextProvider>
                      <AnnotationContextProvider
                        legacyArticleSlug={legacyArticleSlug}
                        levelId={levelId}
                        rawText={rawText}
                        setIsActivityPanelExpanded={setIsActivityPanelExpanded}
                      >
                        {/* fetches all assignments for a user */}
                        <AssignmentsProvider>
                          {/* returns updatedAssignment object and dispatch fn from the reducer */}
                          <AssignmentReducerProvider>
                            <AssignmentActivitySelectionProvider
                              initialActivitySelections={
                                initialActivitySelections
                              }
                            >
                              <GuidedHighlightingStudentAnswerContextProvider>
                                <ClassroomsProvider>
                                  <UserContentUIContextProvider
                                    isLiteContent={isLiteContent}
                                    isPremiumContent={isPremiumContent}
                                  >
                                    <ReadingSkillsContextProvider
                                      currentLevelLanguage={
                                        currentLevelLanguage
                                      }
                                    >
                                      <PollContextProvider
                                        currentAssignment={currentAssignment}
                                        currentLevelLanguage={
                                          currentLevelLanguage
                                        }
                                        selectedAssignmentId={
                                          selectedAssignmentId
                                        }
                                        userProperties={userProperties}
                                      >
                                        <ActivityPanelContextProvider>
                                          {children}
                                        </ActivityPanelContextProvider>
                                      </PollContextProvider>
                                    </ReadingSkillsContextProvider>
                                  </UserContentUIContextProvider>
                                </ClassroomsProvider>
                              </GuidedHighlightingStudentAnswerContextProvider>
                            </AssignmentActivitySelectionProvider>
                          </AssignmentReducerProvider>
                        </AssignmentsProvider>
                      </AnnotationContextProvider>
                    </StudentPreviewContextProvider>
                  </GuidedHighlightingStudentContextProvider>
                </AttachmentsContextProvider>
              </VideoEventBusContextProvider>
            </TranslatorControlsContextProvider>
          </AccessibilityControlsContextProvider>
        </ReadAloudContextProvider>
      </PresentModeContextProvider>
    </FocusManagerContextProvider>
  );
};

export default ViewPageProviders;
